import React, { useEffect, useRef } from "react";
import { IconField } from "primereact/iconfield";
import {
    Controller,
    RegisterOptions,
    useFormContext,
    useFormState,
    useWatch,
    type ControllerRenderProps,
} from "react-hook-form";
import { InputIcon } from "primereact/inputicon";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { InputSwitch } from "primereact/inputswitch";
import { classNames } from "primereact/utils";
import { Tooltip } from "primereact/tooltip";
import "./InputField.scss";
import { v4 as uuidv4 } from "uuid";
import { FilterDropdown } from "../FilterDropdown/FilterDropdown";
import { FilterNativeSelect } from "../FilterNativeSelect/FilterNativeSelect";
import { DashlyAutoComplete } from "../DashlyAutoComplete/DashlyAutoComplete";
import { DashlyDropdown } from "../DashlyDropdown/DashlyDropdown";
import { DashlyFilterDropdown } from "../DashlyFilterDropdown/DashlyFilterDropdown";
import { DashlyFilterMultiSelect } from "../DashlyFilterMultiSelect/DashlyFilterMultiSelect";
import { DashlyMultiSelect } from "../DashlyMultiSelect/DashlyMultiSelect";
import { DashlyNumberInput } from "../DashlyNumberInput/DashlyNumberInput";
import { DashlyPasswordInput } from "../DashlyPasswordInput/DashlyPasswordInput";
import { DashlyTextInput } from "../DashlyTextInput/DashlyTextInput";
import { DashlyTextarea } from "../DashlyTextarea/DashlyTextarea";
import type { DashlyControlVariant } from "../shared/dashlyControlVariant";
import { resolveFieldError } from "./resolveFieldError";
import PickList from "../Picklist/PickList";
import { normalizePickListSelection } from "../../../Util/pickListFormUtils";

const isPickListComponent = (component: React.ElementType) => component === PickList;

export type InputFieldClearVariant = "text" | "select";

export interface InputFieldProps {
    name: string;
    component: React.ElementType;
    rules?: RegisterOptions;
    className?: string;
    onChange?: (e: any) => void;
    onBeforeChange?: (e: any) => Promise<any>;
    disable?: boolean;
    type?: string;
    accept?: string;
    placeholderImage?: string;
    altText?: string;
    customStyle?: React.CSSProperties;
    imageStyle?: React.CSSProperties;
    centered?: boolean;
    tooltip?: boolean;
    pageFilter?: boolean;
    showClear?: boolean;
    clearValue?: unknown;
    onClear?: () => void;
    clearVariant?: InputFieldClearVariant;
    clearAriaLabel?: string;
    forceIconField?: boolean;
    [key: string]: any;
}

const isToggleComponent = (component: React.ElementType) =>
    component === Checkbox || component === InputSwitch;

const isDropdownComponent = (component: React.ElementType) =>
    component === FilterDropdown ||
    component === FilterNativeSelect ||
    component === DashlyFilterDropdown ||
    component === Dropdown ||
    component === DashlyDropdown ||
    component === DashlyMultiSelect ||
    component === DashlyFilterMultiSelect;

const supportsFilterVariant = (component: React.ElementType) =>
    component === DashlyTextInput ||
    component === DashlyDropdown ||
    component === DashlyMultiSelect ||
    component === DashlyFilterDropdown ||
    component === DashlyFilterMultiSelect;

/** Controls that ship their own layout — must not be wrapped in Prime IconField. */
const usesPlainControlWrapper = (component: React.ElementType) =>
    isDropdownComponent(component) ||
    component === DashlyTextInput ||
    component === DashlyPasswordInput ||
    component === DashlyNumberInput ||
    component === DashlyAutoComplete ||
    component === DashlyTextarea ||
    component === Checkbox ||
    component === InputSwitch ||
    component === PickList ||
    (typeof component === 'function' && (
        component.displayName === 'DatasourceSecretField'
        || component.displayName === 'DatasourceListResolveField'
    ));

const supportsPrimeFilterProp = (component: React.ElementType) =>
    isDropdownComponent(component) ||
    component === DashlyAutoComplete ||
    component === DashlyMultiSelect ||
    component === DashlyFilterMultiSelect;

const supportsPrimeShowClearProp = (component: React.ElementType) =>
    component === DashlyDropdown || component === Dropdown;

const InputField: React.FC<InputFieldProps> = ({
    name,
    component: Component,
    rules,
    className,
    onChange,
    disable,
    centered,
    imageStyle,
    tooltip = false,
    pageFilter = false,
    showClear = false,
    clearValue,
    onClear,
    clearVariant = "text",
    clearAriaLabel = "Clear",
    forceIconField = false,
    ...props
}) => {
    const {
        automaticGrid: _automaticGrid,
        defaultValue,
        placeholderImage,
        altText,
        customStyle,
        imageStyle: propsImageStyle,
        accept,
        onBeforeChange,
        filter: filterProp,
        controlVariant: controlVariantProp,
        controlSize,
        invalid: invalidProp,
        className: propsClassName,
        type: inputType,
        ...passThroughProps
    } = props;

    const { control } = useFormContext();
    const { errors } = useFormState({ control, name });
    const error = resolveFieldError(errors, name);
    const hasError = Boolean(error);
    const tooltipRef = useRef(null);
    const hiddenFileInputRef = useRef<HTMLInputElement>(null);
    const inputUuid = uuidv4();
    const errorIconId = `im-input-error-${inputUuid}`;
    const fieldValue = useWatch({ control, name });

    if (!control) {
        throw new Error("InputField must be used within a FormProvider");
    }

    useEffect(() => {
        if (tooltipRef?.current) {
            (tooltipRef.current as any)?.updateTargetEvents();
        }
    }, [error]);

    const getFieldValue = () => {
        if (fieldValue && typeof fieldValue === "object" && fieldValue["name"]) {
            return fieldValue.name;
        }
        return fieldValue;
    };

    const hasClearableValue =
        fieldValue !== null && fieldValue !== undefined && fieldValue !== "";

    const handleClear = (rhfOnChange: (value: unknown) => void) => {
        const emptyValue = clearValue !== undefined ? clearValue : "";
        rhfOnChange(emptyValue);
        if (isDropdown) {
            onChange?.({ value: emptyValue });
        } else {
            onChange?.(emptyValue);
        }
        onClear?.();
    };

    const isDropdown = isDropdownComponent(Component);
    const isMultiSelectComponent =
        Component === DashlyMultiSelect || Component === DashlyFilterMultiSelect;
    /** Filter bars use a custom overlay clear; form dropdowns use Prime's built-in clear. */
    const useCustomDropdownClear =
        isDropdown &&
        showClear &&
        (Component === FilterDropdown ||
            Component === FilterNativeSelect ||
            Component === DashlyFilterDropdown ||
            Component === DashlyFilterMultiSelect);
    const resolvedControlVariant: DashlyControlVariant | undefined =
        pageFilter && supportsFilterVariant(Component)
            ? (controlVariantProp ?? "filter")
            : controlVariantProp;
    /** Prime IconField cloneElement's every child — null/fragments crash the page. */
    const usePlainWrapper =
        pageFilter || showClear || (usesPlainControlWrapper(Component) && !forceIconField);

    const generateFileInput = (field: ControllerRenderProps<any, any>) => {
        const { value, onChange: rhfOnChange, ...restField } = field;

        const containerStyle = {
            ...(customStyle || {}),
            cursor: disable ? "not-allowed" : "pointer",
        };
        const imgStyle = {
            ...((propsImageStyle ?? imageStyle) || {
                width: "100%",
                height: "100%",
                objectFit: "cover" as React.CSSProperties["objectFit"],
            }),
            pointerEvents: (disable ? "none" : "auto") as React.CSSProperties["pointerEvents"],
        };
        const fileInputClasses = classNames(
            "p-avatar-xl",
            {
                "p-invalid": hasError,
                disabled: disable,
                "centered-image": centered,
                "automatic-grid-image": _automaticGrid && !centered,
            },
            propsClassName
        );
        const deletionLabel = passThroughProps["data-deletion-label"] as string | undefined;

        return (
            <div
                className={fileInputClasses}
                style={containerStyle}
                data-deletion-label={deletionLabel}
                onClick={(e) => {
                    if (!disable) {
                        hiddenFileInputRef.current?.click();
                    } else {
                        e.preventDefault();
                    }
                }}
            >
                {value ? (
                    <img key={value} src={value} alt={altText ?? "upload"} style={imgStyle} />
                ) : (
                    <img
                        key="placeholder"
                        src={placeholderImage}
                        alt={altText ?? "placeholder"}
                        style={imgStyle}
                    />
                )}
                {!disable && <i className="pi pi-pencil" />}
                <input
                    {...restField}
                    ref={hiddenFileInputRef}
                    type="file"
                    accept={accept}
                    disabled={disable}
                    style={{ display: "none" }}
                    onChange={async (e: any) => {
                        if (onBeforeChange) {
                            try {
                                const processedEvent = await onBeforeChange(e);
                                if (
                                    processedEvent?.target?.result
                                ) {
                                    rhfOnChange(processedEvent.target.result);
                                    onChange?.(processedEvent.target.result);
                                }
                            } catch (err) {
                                console.error("Error in onBeforeChange:", err);
                            }
                        } else {
                            const files = e.target?.files;
                            if (files?.length > 0) {
                                const file = files[0];
                                const reader = new FileReader();
                                reader.onload = (evt) => {
                                    const result = evt.target?.result;
                                    if (typeof result === "string") {
                                        rhfOnChange(result);
                                        onChange?.(result);
                                    }
                                };
                                reader.readAsDataURL(file);
                            }
                        }
                    }}
                    onClick={(e) => e.stopPropagation()}
                />
            </div>
        );
    };

    const renderClearButton = (rhfOnChange: (value: unknown) => void) => {
        if (!showClear || !hasClearableValue || disable || (isDropdown && !useCustomDropdownClear)) {
            return null;
        }
        return (
            <button
                type="button"
                className="input-field-with-clear__clear"
                aria-label={clearAriaLabel}
                title={clearAriaLabel}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleClear(rhfOnChange);
                }}
            >
                <i className="pi pi-times" aria-hidden />
            </button>
        );
    };

    const handleComponentChange = (
        field: ControllerRenderProps<any, any>,
        e: any
    ) => {
        if (isToggleComponent(Component)) {
            const next = typeof e?.checked === "boolean"
                ? e.checked
                : typeof e?.value === "boolean"
                    ? e.value
                    : Boolean(e?.target?.checked);
            field.onChange(next);
            onChange?.(e);
            return;
        }
        if (typeof e === "string" || typeof e === "number" || typeof e === "boolean") {
            field.onChange(e);
            onChange?.(e);
            return;
        }
        if (Component === DashlyAutoComplete) {
            const next =
                typeof e === "string"
                    ? e
                    : e?.value != null && typeof e.value === "object"
                      ? String((e.value as Record<string, unknown>).value ?? "")
                      : e?.value != null
                        ? String(e.value)
                        : "";
            field.onChange(next);
            onChange?.(next);
            return;
        }
        if (isDropdown) {
            const next = e?.value !== undefined ? e.value : e;
            field.onChange(next);
            onChange?.(e);
            return;
        }
        if (isPickListComponent(Component) && Array.isArray(e?.source) && Array.isArray(e?.target)) {
            field.onChange(e.target);
            onChange?.(e);
            return;
        }
        if (e?.source != null && Array.isArray(e?.source) && Array.isArray(e?.target)) {
            field.onChange(e.target);
            onChange?.(e);
            return;
        } else if (e?.target === undefined && e != null && "value" in e) {
            field.onChange(e.value);
        } else if (
            e != null &&
            typeof e === "object" &&
            "value" in e &&
            typeof e.value === "string" &&
            e.target != null &&
            Object.prototype.hasOwnProperty.call(e.target, "value")
        ) {
            field.onChange(e.value);
        } else if (e?.target !== undefined && e?.target?.name === "icon") {
            field.onChange(
                e.target.value instanceof Object
                    ? "pi pi-" + e.target.value.name
                    : e.target.value
            );
        } else {
            field.onChange(e);
        }
        onChange?.(e);
    };

    const useSelectClearLayout = clearVariant === "select" || useCustomDropdownClear;

    const wrapperClassName = classNames(
        "input-field-with-clear",
        showClear && hasClearableValue && "input-field-with-clear--has-value",
        showClear && useSelectClearLayout && "input-field-with-clear--select",
        error && "input-field-with-clear--invalid",
        className
    );

    return (
        <>
            {tooltip && <Tooltip target={`#im-input-${inputUuid}`} />}
            <div className={wrapperClassName}>
                <Controller
                    name={name}
                    control={control}
                    rules={rules}
                    defaultValue={defaultValue ?? (isToggleComponent(Component) ? false : "")}
                    render={({ field }) => {
                        const { onChange: rhfOnChange, value, ref, ...restField } = field;
                        const pickListTarget = isPickListComponent(Component)
                            ? normalizePickListSelection(value)
                            : value;

                        if (inputType === "file") {
                            return generateFileInput(field);
                        }

                        const sharedComponentProps = {
                            ...restField,
                            ...passThroughProps,
                            ...(resolvedControlVariant !== undefined
                                ? { controlVariant: resolvedControlVariant }
                                : {}),
                            ...(controlSize !== undefined ? { controlSize } : {}),
                            ...(supportsPrimeShowClearProp(Component) &&
                            showClear &&
                            !useCustomDropdownClear
                                ? { showClear }
                                : {}),
                            ...(supportsPrimeFilterProp(Component)
                                ? { filter: Boolean(pageFilter || filterProp) }
                                : {}),
                            value: pickListTarget,
                            checked: value,
                            disabled: disable,
                            invalid: hasError || invalidProp,
                            ...(isPickListComponent(Component) ? { target: pickListTarget } : {}),
                            className: classNames(
                                { "p-invalid": hasError },
                                usesPlainControlWrapper(Component) && Component !== InputSwitch && "w-full",
                                propsClassName,
                            ),
                            onTextChange: async (e: any) => {
                                field.onChange(e.htmlValue);
                            },
                            onChange: (e: any) => handleComponentChange(field, e),
                        };

                        if (usePlainWrapper) {
                            return (
                                <div
                                    ref={ref}
                                    id={`im-input-${inputUuid}`}
                                    className={classNames(
                                        "input-field-with-clear__field-wrap",
                                        isDropdown &&
                                            !isMultiSelectComponent &&
                                            "input-field-with-clear__dropdown-wrap",
                                        isMultiSelectComponent && "input-field-with-clear__multiselect-wrap",
                                    )}
                                    data-pr-tooltip={getFieldValue()}
                                >
                                    <Component {...sharedComponentProps} />
                                    {renderClearButton(rhfOnChange)}
                                </div>
                            );
                        }

                        return (
                            <div
                                ref={ref}
                                id={`im-input-${inputUuid}`}
                                className="input-field-with-clear__field-wrap"
                                data-pr-tooltip={getFieldValue()}
                            >
                                <IconField className="input-field-with-clear__field">
                                    <Component {...sharedComponentProps} />
                                    {error !== undefined ? (
                                        <InputIcon
                                            id={errorIconId}
                                            ref={tooltipRef}
                                            style={{ color: "red" }}
                                            data-pr-tooltip={error?.message?.toString()}
                                            data-pr-position="right"
                                            className="pi pi-exclamation-triangle"
                                        />
                                    ) : (
                                        <span className="input-field-with-clear__icon-spacer" aria-hidden />
                                    )}
                                </IconField>
                            </div>
                        );
                    }}
                />
            </div>
            {error !== undefined && !usePlainWrapper ? (
                <Tooltip target={`#${errorIconId}`} />
            ) : null}
        </>
    );
};

export { InputField };
